import React, { useEffect, useState } from 'react'
import {
  RouteComponentProps,
  Redirect,
  BrowserRouter as Router,
  Route,
  Switch,
  RouteProps,
} from 'react-router-dom'

import DefaultHomePage from './DefaultHomePage'
import DownloadsPage from './DownloadsPage'
import ErrorBoundary from './ErrorBoundary'
import GenePage from './GenePage/GenePage'
import GeneResultsPage from './GeneResultsPage/GeneResultsPage'
import InfoPage from './InfoPage'
import OtherStudies from './OtherStudies'
import PageNotFoundPage from './PageNotFoundPage'
import TopBar from './TopBar'
import vepConsequences from './vepConsequences'
import LoginPage from './LoginPage'
import { userHasBearerCookie } from './auth'
import { GeneRow } from './GeneResultsPage/geneResultTableColumns'
import {
  ConsequenceCategory,
  VariantCategoryOption,
  VariantConsequence,
  VariantConsequenceCategoryLabels,
  DEFAULT_VARIANT_CATEGORY_OPTIONS,
} from './variantCategories'

export type DatasetId = 'AGB' | 'BipEx' | 'Epi25' | 'GP2' | 'IBD' | 'SCHEMA'

export type ReferenceGenome = 'GRCh37' | 'GRCh38'

interface DatasetFields {
  reference_genome: ReferenceGenome
  gene_result_analysis_groups: string[]
  gene_group_result_field_names: string[]
  gene_group_result_field_types: string[]
  variant_result_analysis_groups: string[]
  variant_group_result_field_names: string[]
  variant_group_result_field_types: string[]
  variant_info_field_names: string[]
  variant_info_field_types: string[]
}

declare global {
  interface Window {
    datasetConfig: {
      datasetId: DatasetId
      reference_genome: ReferenceGenome
      datasets: Record<DatasetId, DatasetFields>
      gene_result_analysis_groups: string[]
      gene_group_result_field_names: string[]
      gene_group_result_field_types: string[]
      variant_result_analysis_groups: string[]
      variant_fields: string[]
      variant_group_result_field_names: string[]
      variant_group_result_field_types: string[]
      variant_info_field_names: string[]
      variant_info_field_types: string[]
      clinvar?: {
        variant_fields: string[]
        variant_info_field_names: string[]
        variant_info_field_types: string[]
      }
    }
    gaTrackingId: string
    gtag: Gtag.Gtag
  }
}

const PASSWORD_PROTECTED_DATASETS: DatasetId[] = []

interface ProtectedRouteExtraProps {
  datasetId: DatasetId
  isAuthenticated: boolean
}

interface ProtectedRouteProps extends Omit<RouteProps, 'component' | 'render'> {
  component?: React.ComponentType<RouteComponentProps<any> & ProtectedRouteExtraProps>
  render?: (props: RouteComponentProps<any> & ProtectedRouteExtraProps) => React.ReactNode
  isAuthenticated: boolean
  datasetId: DatasetId
}

const ProtectedRoute = ({
  component: Component = undefined,
  render: renderFunc = undefined,
  isAuthenticated,
  datasetId,
  ...rest
}: ProtectedRouteProps) => {
  return (
    <Route
      {...rest}
      render={(routeProps: RouteComponentProps) => {
        if (PASSWORD_PROTECTED_DATASETS.includes(datasetId) && !isAuthenticated) {
          return (
            <Redirect
              to={{
                pathname: '/login',
                state: { from: routeProps.location },
              }}
            />
          )
        }

        if (Component) {
          return (
            <Component
              {...routeProps}
              {...rest}
              datasetId={datasetId}
              isAuthenticated={isAuthenticated}
            />
          )
        }

        if (renderFunc) {
          return renderFunc({
            ...routeProps,
            ...rest,
            datasetId,
            isAuthenticated,
          })
        }

        return null
      }}
    />
  )
}

type ExtraPage = {
  path: string
  label: string
  component: React.ComponentType<any>
}

export type GeneResultColumnGroup = {
  key: string
  geneTableLabel: string
  variantTableLabel: string
  color?: string
}

export type GeneResultColumnConfig<RowType = any, ValueType = any> = {
  centerCellContent?: boolean
  centerNumericValues?: boolean
  centerHeading?: boolean
  // `key` is both for labeling the CSV export column, and for use in a lodash get call when not using the optional `accessor` prop to get data
  key: string
  heading?: React.ReactNode
  minWidth?: number
  tooltip?: string
  group?: GeneResultColumnGroup
  visibilityGroup?: GeneResultColumnGroup
  accessor?: (row: RowType) => ValueType
  render?: (value: ValueType, row?: RowType) => React.ReactNode
  renderForCSV?: (value: ValueType, row?: RowType) => string | number
}

export type GeneResultTabConfig = {
  id: string
  label: string
  render: (record: GeneRow[]) => React.ReactNode
}

export type VariantColumnConfig<RowType = any, ValueType = any> = {
  centerCellContent?: boolean
  centerHeading?: boolean
  // Require a plain-text renderer to measure centered numeric values.
  centerNumericValues?: boolean
  // `key` is both for labeling the CSV export column, and for use in a lodash get call when not using the optional `accessor` prop to get data
  key: string
  heading?: string
  minWidth?: number
  tooltip?: string
  accessor?: (row: RowType) => ValueType
  render?: (value: ValueType) => React.ReactNode
  renderForCSV?: (value: ValueType) => string | number
  showOnGenePage?: boolean
  showOnDetails?: boolean
  isSortable?: boolean
  group?: GeneResultColumnGroup
  sortFunction?: (a: ValueType, b: ValueType) => number
  sortKey?: string
  type?: string
}

// Re-exported from a leaf module with no other project imports; see variantCategories.ts
// for why (files that only need e.g. `DEFAULT_VARIANT_CATEGORY_OPTIONS` must not be forced
// to pull in this whole module's import graph, which has dataset-config side effects).
export * from './variantCategories'

export type VariantCustomFilter<RowType = any> = {
  component: React.ElementType
  defaultFilter: any
  applyFilter: (variants: RowType[], filterState: any) => RowType[]
}

export type VariantLollipopTrack = {
  key: string
  title: string
  acField: string
  height?: number
}

export type VariantLollipopTrackGroup = {
  key: string
  label: string
  tooltip?: string
  color?: string
  tracks: [VariantLollipopTrack, VariantLollipopTrack]
}
// Receives a variant's `info` object, not the full variant row.
export type RenderVariantAttributes<InfoType = any> = (
  info: InfoType
) => { label: React.ReactNode; content: React.ReactNode }[]

type BrowserProps = {
  browserTitle?: string
  navBarBackgroundColor?: string
  homePage?: React.ComponentType<any>
  extraPages?: ExtraPage[]

  geneResultsPageHeading?: string
  geneResultAnalysisGroupOptions?: readonly string[]
  defaultGeneResultAnalysisGroup?: string
  defaultGeneResultSortKey?: string
  defaultGeneResultSortOrder?: string
  defaultGeneResultColumnGroups?: Record<string, boolean>
  geneResultColumns?: GeneResultColumnConfig[]
  geneResultTabs?: GeneResultTabConfig[]
  showManeSelectTranscript?: boolean

  variantAnalysisGroupOptions: readonly string[]
  defaultVariantAnalysisGroup: string
  variantAnalysisGroupLabels?: Record<string, string>
  variantResultColumns?: VariantColumnConfig[]
  defaultVariantTableSortKey?: string
  defaultVariantTableSortOrder?: string
  variantConsequences?: VariantConsequence[]
  variantConsequenceCategoryLabels?: VariantConsequenceCategoryLabels
  variantCategoryOptions?: VariantCategoryOption[]
  getVariantCategory?: (variant: any) => string
  variantCustomFilter?: VariantCustomFilter
  variantDetailColumns?: VariantColumnConfig[]
  variantDetailsSingleAnalysisGroup?: boolean
  renderVariantAttributes?: RenderVariantAttributes
  additionalVariantDetailSummaryColumns?: VariantColumnConfig[]
  renderVariantTranscriptConsequences?: boolean
  variantAlleleFrequencyOverride?: number
  variantExportNote?: string
  variantLollipopTrackGroups?: VariantLollipopTrackGroup[]
}

const Browser = ({
  browserTitle = 'Results Browser',
  navBarBackgroundColor = '#000',
  // Pages
  homePage = DefaultHomePage,
  extraPages = [],
  // Gene results
  geneResultsPageHeading = 'Results',
  geneResultAnalysisGroupOptions = undefined,
  defaultGeneResultAnalysisGroup = undefined,
  defaultGeneResultSortKey = 'gene_symbol',
  defaultGeneResultSortOrder = undefined,
  defaultGeneResultColumnGroups = undefined,
  geneResultColumns = [],
  geneResultTabs = [],
  showManeSelectTranscript = false,
  // Variant results
  defaultVariantAnalysisGroup,
  variantAnalysisGroupOptions,
  variantAnalysisGroupLabels = undefined,
  variantResultColumns = [],
  defaultVariantTableSortKey = undefined,
  defaultVariantTableSortOrder = undefined,
  variantConsequences = vepConsequences,
  variantConsequenceCategoryLabels = {
    lof: 'LoF',
    missense: 'Missense',
    synonymous: 'Synonymous',
    other: 'Other',
  },
  variantCategoryOptions = DEFAULT_VARIANT_CATEGORY_OPTIONS,
  getVariantCategory = undefined,
  variantCustomFilter = undefined,
  renderVariantAttributes = undefined,
  additionalVariantDetailSummaryColumns = undefined,
  variantDetailColumns = undefined,
  variantDetailsSingleAnalysisGroup = false,
  renderVariantTranscriptConsequences = false,
  variantAlleleFrequencyOverride = undefined,
  variantExportNote = undefined,
  variantLollipopTrackGroups = undefined,
}: BrowserProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const { datasetId } = window.datasetConfig

  useEffect(() => {
    setIsAuthenticated(userHasBearerCookie())
    setIsAuthLoading(false)
  }, [])

  if (isAuthLoading) {
    return <div>Loading...</div>
  }

  return (
    <Router>
      <TopBar
        title={browserTitle}
        links={extraPages.map(({ path, label }) => ({ path, label }))}
        backgroundColor={navBarBackgroundColor}
        passwordProtectedDatasets={PASSWORD_PROTECTED_DATASETS}
      />

      {window.gtag && (
        <Route
          path="/"
          render={({ location }) => {
            window.gtag('config', window.gaTrackingId, {
              page_path: location.pathname,
            })
            return null
          }}
        />
      )}

      <ErrorBoundary>
        <Switch>
          <Route path="/login" exact component={LoginPage} />

          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            datasetId={datasetId}
            path="/"
            exact
            component={homePage}
          />

          {datasetId !== 'GP2' && (
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              datasetId={datasetId}
              path="/results"
              render={() => (
                <GeneResultsPage
                  browserTitle={browserTitle}
                  analysisGroupOptions={geneResultAnalysisGroupOptions}
                  defaultAnalysisGroup={defaultGeneResultAnalysisGroup}
                  defaultSortKey={defaultGeneResultSortKey}
                  defaultSortOrder={defaultGeneResultSortOrder}
                  defaultColumnGroups={defaultGeneResultColumnGroups}
                  geneResultColumns={geneResultColumns}
                  pageHeading={geneResultsPageHeading}
                  tabs={geneResultTabs}
                />
              )}
            />
          )}

          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            datasetId={datasetId}
            path="/gene/:gene"
            render={({ match }) => (
              <GenePage
                showManeSelectTranscript={showManeSelectTranscript}
                datasetId={datasetId}
                geneIdOrSymbol={match.params.gene}
                browserTitle={browserTitle}
                defaultVariantAnalysisGroup={defaultVariantAnalysisGroup}
                variantAnalysisGroupOptions={variantAnalysisGroupOptions}
                variantAnalysisGroupLabels={variantAnalysisGroupLabels}
                variantResultColumns={variantResultColumns}
                variantSortKey={defaultVariantTableSortKey}
                variantSortOrder={defaultVariantTableSortOrder}
                variantConsequences={variantConsequences}
                variantConsequenceCategoryLabels={variantConsequenceCategoryLabels}
                variantCategoryOptions={variantCategoryOptions}
                getVariantCategory={getVariantCategory}
                variantCustomFilter={variantCustomFilter}
                renderVariantAttributes={renderVariantAttributes}
                additionalVariantDetailSummaryColumns={additionalVariantDetailSummaryColumns}
                variantDetailColumns={variantDetailColumns}
                variantDetailsSingleAnalysisGroup={variantDetailsSingleAnalysisGroup}
                renderVariantTranscriptConsequences={renderVariantTranscriptConsequences}
                variantAlleleFrequencyOverride={variantAlleleFrequencyOverride}
                variantExportNote={variantExportNote}
                variantLollipopTrackGroups={variantLollipopTrackGroups}
              />
            )}
          />

          {extraPages.map(({ path, component }) => (
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              datasetId={datasetId}
              key={path}
              path={path}
              component={component}
            />
          ))}

          {datasetId !== 'GP2' && (
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              datasetId={datasetId}
              path="/downloads"
              component={DownloadsPage}
            />
          )}

          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            datasetId={datasetId}
            path="/other-studies"
            render={() => (
              <InfoPage title="Other Studies">
                <OtherStudies />
              </InfoPage>
            )}
          />

          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            datasetId={datasetId}
            component={PageNotFoundPage}
          />
        </Switch>
      </ErrorBoundary>
    </Router>
  )
}

export default Browser
